import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package, Plus, Search } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import { getAllLoads, searchLoads } from '../api/loadsApi';
import { useToast } from '../context/ToastContext';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'awaiting', label: 'Awaiting' },
  { value: 'picked up', label: 'Picked Up' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'canceled', label: 'Canceled' },
];

function Loads() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const driverIdFilter = searchParams.get('driver_id');

  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchLoads = async (pageNum = 1, append = false, status = statusFilter) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError('');

    try {
      const result = await getAllLoads(pageNum, 10, status);
      if (result.success) {
        setLoads((prev) => (append ? [...prev, ...result.loads] : result.loads));
        setHasMore(result.has_more);
        setPage(pageNum);
      } else {
        setError(result.message || t('common.genericError'));
        showToast('error', result.message || t('common.genericError'));
      }
    } catch (err) {
      setError(t('common.networkError'));
      showToast('error', t('common.networkError'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const runSearch = async (query, status) => {
    setLoading(true);
    setError('');
    try {
      const results = await searchLoads(query, status);
      setLoads(results);
      setHasMore(false);
    } catch (err) {
      setError(t('common.networkError'));
      showToast('error', t('common.networkError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchQuery.trim()) {
        runSearch(searchQuery.trim(), statusFilter);
      } else {
        fetchLoads(1, false, statusFilter);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter]);

  const displayedLoads = driverIdFilter
    ? loads.filter((l) => String(l.driver_assigned_id) === driverIdFilter)
    : loads;

  const columns = [
    {
      accessorKey: 'id_load',
      header: 'Load #',
      cell: (info) => (
        <span
          style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 700 }}
          onClick={() => navigate(`/loads/${info.getValue()}`)}
        >
          #{info.getValue()}
        </span>
      ),
    },
    {
      accessorKey: 'origin',
      header: 'Route',
      cell: (info) => (
        <span>
          {info.row.original.origin} <span style={{ color: '#9ca3af' }}>→</span> {info.row.original.destination}
        </span>
      ),
    },
    {
      accessorKey: 'driver_name',
      header: 'Driver',
      cell: (info) => info.getValue() || <span style={{ color: '#9ca3af' }}>Unassigned</span>,
    },
    {
      accessorKey: 'load_status',
      header: 'Status',
      cell: (info) => <StatusBadge status={info.getValue()} />,
    },
    {
      accessorKey: 'price_value',
      header: 'Price',
      cell: (info) =>
        info.getValue() != null
          ? `${Number(info.getValue()).toLocaleString()} ${info.row.original.price_currency}`
          : '—',
    },
    {
      accessorKey: 'pick_up_date',
      header: 'Pick Up',
    },
    {
      accessorKey: 'delivery_date',
      header: 'Delivery',
    },
  ];

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div style={{ flex: 1 }}>
        <TopBar
          title={t('nav.loads')}
          rightAction={
            <button onClick={() => navigate('/loads/create')} style={styles.createBtn}>
              <Plus size={16} style={{ marginRight: 6 }} />
              Create Load
            </button>
          }
        />

        <div style={{ padding: 24 }}>
          {driverIdFilter && (
            <div style={styles.filterBanner}>
              Showing loads for this driver only.{' '}
              <span
                onClick={() => navigate('/loads')}
                style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 700 }}
              >
                Clear filter
              </span>
            </div>
          )}

          <div style={styles.filtersRow}>
            <div style={styles.searchWrapper}>
              <Search size={16} style={styles.searchIcon} />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by origin, destination, or load #"
                style={styles.searchInput}
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={styles.statusSelect}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {loading && <LoadingSpinner fullScreen />}

          {!loading && error && (
            <EmptyState
              icon={Package}
              title={t('common.genericError')}
              message={error}
              onRetry={() =>
                searchQuery ? runSearch(searchQuery, statusFilter) : fetchLoads(1, false, statusFilter)
              }
              retryLabel={t('common.tryAgain')}
            />
          )}

          {!loading && !error && displayedLoads.length === 0 && (
            <EmptyState
              icon={Package}
              title="No loads found"
              message={
                searchQuery || statusFilter
                  ? 'Try a different search term or status.'
                  : driverIdFilter
                  ? 'No loads for this driver yet.'
                  : 'Create your first load to get started.'
              }
            />
          )}

          {!loading && !error && displayedLoads.length > 0 && (
            <>
              <DataTable columns={columns} data={displayedLoads} />

              {!searchQuery && !driverIdFilter && hasMore && (
                <button
                  onClick={() => fetchLoads(page + 1, true, statusFilter)}
                  disabled={loadingMore}
                  style={styles.loadMoreBtn}
                >
                  {loadingMore ? t('common.loading') : t('common.loadMore')}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  createBtn: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    padding: '9px 16px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  filterBanner: {
    backgroundColor: '#eff6ff',
    color: '#1e40af',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 16,
  },
  filtersRow: {
    display: 'flex',
    gap: 10,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  searchWrapper: {
    position: 'relative',
    maxWidth: 420,
    flex: 1,
    minWidth: 240,
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    top: 12,
    color: '#9ca3af',
  },
  searchInput: {
    width: '100%',
    padding: '10px 12px 10px 36px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  statusSelect: {
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    fontSize: 14,
    outline: 'none',
    backgroundColor: 'white',
  },
  loadMoreBtn: {
    width: '100%',
    padding: '12px 0',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    color: '#2563eb',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    marginTop: 12,
  },
};

export default Loads;